import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { History, Mail } from "lucide-react";
import { isAppleSignInAvailable } from "../../../native/platform";
import {
  readLastAuthMethod,
  rememberReturnPath,
  returnPathFrom,
  useAuthWithFirebase,
  type AuthMethod,
} from "../../../hooks/useAuth";
import { extraProvidersEnabled, signInWithExtraProvider, type ExtraProvider } from "../../../firebase/extraProviders";
import { reportOAuthError } from "../../../firebase/oauthErrors";
import { AppleLoginButton } from "../AppleLoginButton";
import GoogleLoginButton from "../GoogleLoginButton";

const METHOD_LABEL: Record<AuthMethod, string> = {
  email: "email",
  google: "Google",
  apple: "Apple",
  link: "email",
  microsoft: "Microsoft",
  facebook: "Facebook",
};

/**
 * Other ways in, under the Login / Register form: an "— или —" divider, then
 * one row of Google, Apple (iOS app only — see isAppleSignInAvailable()) and
 * the passwordless email link, so the whole page fits one screen; optional
 * Microsoft/Facebook below. The last-used method gets a corner badge on its
 * button instead of a line of its own.
 */
export function SocialAuthButtons() {
  const { t } = useTranslation();
  const location = useLocation();
  const apple = isAppleSignInAvailable();
  const extra = extraProvidersEnabled();
  const last = readLastAuthMethod();

  return (
    <div className="mt-3 auth-tall:mt-4">
      <div className="mb-3 auth-short:mb-2.5 flex items-center gap-3 text-[13px] font-medium text-gray-400 auth-tall:mb-4">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-200" />
        {t("authFlow.orWith", "или")}
        <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-200" />
      </div>

      {/* Social providers may leave the page (iOS redirect) — keep where to come back to. */}
      <div onClickCapture={() => rememberReturnPath(returnPathFrom(location.state))}>
        <div className={`grid gap-2.5 ${apple ? "grid-cols-3" : "grid-cols-2"}`}>
          <LastUsed method="google" last={last}>
            <GoogleLoginButton compact />
          </LastUsed>
          {apple && (
            <LastUsed method="apple" last={last}>
              <AppleLoginButton compact />
            </LastUsed>
          )}
          <LastUsed method="link" last={last}>
            <EmailLinkButton inline />
          </LastUsed>
        </div>
        {extra.length > 0 && (
          <div className={`mt-2.5 grid gap-2.5 ${extra.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
            {extra.map((p) => (
              <LastUsed key={p} method={p} last={last}>
                <ExtraProviderButton provider={p} />
              </LastUsed>
            ))}
          </div>
        )}
      </div>

      {/* Password users have no button to badge — say it in a line under the buttons. */}
      {last === "email" && (
        <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-500">
          <History size={12} className="shrink-0 text-blue-600" />
          {t("authFlow.lastUsed", { method: METHOD_LABEL[last], defaultValue: "В прошлый раз вы входили через {{method}}" })}
        </p>
      )}
    </div>
  );
}

/** Marks the provider used last time with a small corner badge (full sentence in the tooltip). */
function LastUsed({ method, last, children }: { method: AuthMethod; last: AuthMethod | null; children: ReactNode }) {
  const { t } = useTranslation();
  if (last !== method) return <>{children}</>;
  const full = t("authFlow.lastUsed", { method: METHOD_LABEL[method], defaultValue: "В прошлый раз вы входили через {{method}}" });
  return (
    <div className="relative">
      {children}
      <span
        title={full}
        className="pointer-events-none absolute -top-2 right-2 flex items-center gap-1 rounded-full bg-blue-600 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-white shadow-sm shadow-blue-600/30"
      >
        <History size={10} />
        {t("authFlow.lastShort", "Прошлый раз")}
      </span>
      <span className="sr-only">{full}</span>
    </div>
  );
}

/**
 * Any email provider, no password: Firebase email link. `inline` is the
 * short tile in the social row; the full-width variant stays for other spots.
 */
export function EmailLinkButton({ inline = false }: { inline?: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  if (inline) {
    return (
      <Link
        to="/email-link"
        state={location.state}
        title={t("authFlow.linkButton", "Войти по ссылке на почту")}
        className="flex h-11 auth-tall:h-12 w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-1.5 text-center text-[13px] sm:text-[14px] font-semibold leading-tight text-gray-800 shadow-sm shadow-gray-900/[.03] transition hover:border-blue-200 hover:bg-blue-50/50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15 active:scale-[.99]"
      >
        <Mail size={16} className="shrink-0 text-blue-600" />
        <span className="line-clamp-2">{t("authFlow.linkShort", "Ссылка на почту")}</span>
      </Link>
    );
  }
  return (
    <Link
      to="/email-link"
      state={location.state}
      className="group mt-3 flex min-h-12 w-full items-center justify-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-center text-[14px] sm:text-[15px] font-semibold text-gray-800 shadow-sm shadow-gray-900/[.03] transition hover:border-blue-200 hover:bg-blue-50/50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15 active:scale-[.99]"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition group-hover:bg-blue-100">
        <Mail size={16} />
      </span>
      {t("authFlow.linkButton", "Войти по ссылке на почту")}
    </Link>
  );
}

function ExtraProviderButton({ provider }: { provider: ExtraProvider }) {
  const mutation = useAuthWithFirebase(provider);
  const [signingIn, setSigningIn] = useState(false);
  const busy = signingIn || mutation.isPending;
  const label = provider === "microsoft" ? "Microsoft" : "Facebook";

  const onClick = async () => {
    if (busy) return;
    setSigningIn(true);
    try {
      const token = await signInWithExtraProvider(provider);
      if (token) mutation.mutate(token);
    } catch (error) {
      reportOAuthError(provider, error);
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="flex h-11 auth-tall:h-12 w-full items-center justify-center gap-2.5 rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-900/[.03] transition hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15 active:scale-[.99] disabled:opacity-60"
    >
      {busy ? (
        <span className="auth-spinner h-4 w-4 rounded-full border-2 border-gray-300 border-t-gray-600" />
      ) : provider === "microsoft" ? (
        <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#7FBA00" d="M12 1h10v10H12z" />
          <path fill="#00A4EF" d="M1 12h10v10H1z" />
          <path fill="#FFB900" d="M12 12h10v10H12z" />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="#1877F2"
            d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.32l-.53 3.5h-2.8V24C19.62 23.1 24 18.1 24 12.07z"
          />
        </svg>
      )}
      <span className="text-[15px] font-semibold text-gray-800">{label}</span>
    </button>
  );
}
